import { css } from '@emotion/react';
import {
  Box,
  Button,
  EmptyStates,
  Flex,
  LoadingOverlay,
  Pagination,
  Stack,
} from '@inflearn/ds-react-next';
import type { Language } from '@inflearn/i18n-tools';
import { SuspenseQuery } from '@inflearn/query';
import { ClipCard } from '@inflearn/ui';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { parseAsInteger, useQueryStates } from 'nuqs';
import qs from 'query-string';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { NEXT_PUBLIC_CDN_URL } from '../../../../appConfig';
import {
  API_ENDPOINT,
  fetchClientApiV1InstructorsUserIdClips,
} from '../../../../resources/@types/uccSchema';
import { logger } from '../../../../resources/Logger';
import CustomHead from '../../../components/layout/CustomHead';
import Layout from '../../../components/layout/Layout';
import SectionHeader from '../../../components/pages/profile/SectionHeader/SectionHeader';
import { COURSE_URL, USERS_URL } from '../../../constants/urls';
import useCheckBlackUser from '../../../hooks/useCheckBlackUser';
import useCheckMyProfile from '../../../hooks/useCheckMyProfile';
import useUserProfileQuery from '../../../hooks/useUserProfileQuery';
import { useClipsCreateMutation } from '../../../mutations/useClipsCreateMutation';
import {
  getInstructorsUserIdClipsQueryKey,
  getInstructorsUserIdClipsQueryOptions,
} from '../../../queries/useInstructorsUserIdClipsQuery';
import dayjs from '../../../utils/date';
import {
  type WithCommonNextPage,
  withProfileUserProps,
} from '../../../utils/page/withProfileUserProps';
import { withRemovedPageFallback } from '../../../utils/page/withRemovedPageFallback';

const CLIP_ITEM_PAGE_LIMIT = 10;

type UserClipsPageProps = {
  serverNow: string;
};

// nuqs의 parseAsInteger와 동일한 parseInt 규칙으로 파싱해, SSR과 클라이언트가 항상 같은 페이지 번호를 쓰도록 맞춘다.
function normalizePageNumber(value: unknown) {
  const parsed = parseInt(String(value ?? ''), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

const UserClipsPage: WithCommonNextPage<UserClipsPageProps> = ({ serverNow }) => {
  const { t } = useTranslation();

  const [queryString, setQueryString] = useQueryStates(
    {
      pageNumber: parseAsInteger.withDefault(1),
    },
    {
      history: 'push',
      shallow: true,
      clearOnDefault: true,
    },
  );
  const pageNumber = normalizePageNumber(queryString.pageNumber);

  const { query } = useRouter();
  const { data: profile } = useUserProfileQuery({ id: Number(query.id) });

  const { mutate: createClip } = useClipsCreateMutation({});

  const { isMe } = useCheckMyProfile();
  const { isBlackUser } = useCheckBlackUser();

  const userName = profile?.name;

  const handlePageChange = (pageNumber: number) => {
    setQueryString({ pageNumber });
  };

  return (
    <Layout isInstructor={Boolean(profile?.isInstructor)} isInactive={Boolean(profile?.isInactive)}>
      <CustomHead
        title={`${userName}님의 클립`}
        description={`인프런 ${userName}님의 클립 페이지 입니다. - ${userName}님 소개 | 인프런`}
        image={profile?.thumbnailUrl}
      />
      <Box pos="relative">
        <ErrorBoundary fallback={null}>
          <Suspense fallback={<LoadingOverlay visible h={400} />}>
            <SuspenseQuery
              {...getInstructorsUserIdClipsQueryOptions({
                userId: Number(query.id),
                params: {
                  excludeClipIds: [],
                  pageNumber,
                  pageSize: CLIP_ITEM_PAGE_LIMIT,
                },
                options: {
                  keepPreviousData: true,
                },
              })}
            >
              {({ data }) => {
                const clips = data.data.items || [];
                const totalCount = data.data.totalCount;

                return (
                  <>
                    <SectionHeader styles={css({ marginBottom: '30px !important' })}>
                      <SectionHeader.Title title={'클립'}>
                        {totalCount > 0 ? (
                          <SectionHeader.TotalCount totalCount={totalCount} />
                        ) : null}
                      </SectionHeader.Title>
                      {isMe && !isBlackUser ? (
                        <Button
                          variant="default"
                          size="sm"
                          radius="md"
                          onClick={() => {
                            createClip({});
                          }}
                        >
                          {t('작성하기')}
                        </Button>
                      ) : null}
                    </SectionHeader>
                    {totalCount === 0 ? (
                      <EmptyStates image="NO_DATA" title={'작성된 클립이 없습니다.'} />
                    ) : (
                      <Stack gap={24}>
                        {clips.map((clip) => (
                          <ClipCard.Article
                            key={clip.id}
                            cdnUrl={NEXT_PUBLIC_CDN_URL || ''}
                            {...clip}
                            anchorProps={{
                              href: COURSE_URL.CLIP_CLIP_ID({
                                clipId: clip.id,
                              }),
                            }}
                            now={dayjs(serverNow)}
                          />
                        ))}
                      </Stack>
                    )}
                    <Flex justify="center" py={32}>
                      <Pagination
                        onChange={handlePageChange}
                        withControls
                        size="md"
                        value={pageNumber}
                        total={data.data.totalPage}
                      />
                    </Flex>
                  </>
                );
              }}
            </SuspenseQuery>
          </Suspense>
        </ErrorBoundary>
      </Box>
    </Layout>
  );
};

export const getServerSideProps = withProfileUserProps(
  async ({ context, queryClient, isInstructor }) => {
    const userId = context.params?.id ? String(context.params?.id) : '';
    const lang = context.locale as Language;
    const serverNow = dayjs().toISOString();

    if (!isInstructor) {
      return {
        redirect: {
          destination: USERS_URL.HOME({ userId: userId, lang }),
          permanent: false,
        },
      };
    }

    const pageNumber = normalizePageNumber(context.query.pageNumber);

    const [clipsResult] = await Promise.allSettled([
      queryClient.fetchQuery(
        getInstructorsUserIdClipsQueryKey({
          userId: Number(userId),
          params: {
            excludeClipIds: [],
            pageNumber,
            pageSize: CLIP_ITEM_PAGE_LIMIT,
          },
        }),
        () =>
          fetchClientApiV1InstructorsUserIdClips(
            qs.stringifyUrl({
              url: API_ENDPOINT.CLIENT_V1_INSTRUCTORS_USERID_CLIPS(userId),
              query: {
                excludeClipIds: [],
                pageNumber,
                pageSize: CLIP_ITEM_PAGE_LIMIT,
              },
            }),
          ),
      ),
    ]);

    if (clipsResult.status === 'rejected') {
      logger.error({
        userId,
        page: 'UserClips',
        location: 'getServerSideProps',
        message:
          clipsResult.reason instanceof Error
            ? clipsResult.reason.message
            : '클립 목록 프리페치 실패',
      });
    }

    return {
      props: { serverNow },
    };
  },
);

export default withRemovedPageFallback(UserClipsPage);
